import { ReactNode, useEffect, useState } from 'react';

import { BitsButton, BitsButtonProps } from '../bits-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../dialog';

interface ConfirmDialogProps {
  title?: string;
  description?: ReactNode;
  variant?: 'default' | 'destructive';
  open: boolean;
  onOpenChange: (open: boolean, force?: boolean) => void;
  buttons: BitsButtonProps[];
  closable?: boolean;
}

const ConfirmDialog = ({
  title = 'Are you sure?',
  description = 'This action cannot be undone.',
  open,
  onOpenChange,
  buttons,
  closable = true,
}: ConfirmDialogProps) => {
  const [currentButtonLoading, setCurrentButtonLoading] = useState<
    number | null
  >(null);

  const handleClickOption = async (
    callback: BitsButtonProps['onClick'],
    event: React.MouseEvent<HTMLButtonElement>,
    index: number
  ) => {
    setCurrentButtonLoading(index);
    await callback?.(event);
    setCurrentButtonLoading(null);
    onOpenChange(false, true);
  };

  useEffect(() => {
    if (!open) {
      setCurrentButtonLoading(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={currentButtonLoading === null ? onOpenChange : undefined}
    >
      <DialogContent
        closeButton={currentButtonLoading === null ? closable : false}
        variant="sm"
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <DialogDescription asChild>
            <div>{description}</div>
          </DialogDescription>

          <div className="flex gap-4">
            {buttons.map(({ ...buttonProps }, index) => (
              <BitsButton
                key={index}
                {...buttonProps}
                disabled={currentButtonLoading !== null}
                loading={currentButtonLoading === index}
                onClick={(event) =>
                  handleClickOption(buttonProps.onClick, event, index)
                }
                className="w-full"
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

type UseConfirmDialogTriggerOptions = Pick<
  ConfirmDialogProps,
  'title' | 'description' | 'buttons' | 'closable'
>;

type ConfirmDialogState = {
  open: boolean;
  options: UseConfirmDialogTriggerOptions;
  awaitedCallback: (() => Promise<unknown>) | null;
};

type ConfirmDialogSingleton = {
  state: ConfirmDialogState;
  listeners: Set<() => void>;
  setState: (newState: Partial<ConfirmDialogState>) => void;
  subscribe: (listener: () => void) => () => void;
};

// Singleton implementation
const confirmDialogSingleton: ConfirmDialogSingleton = {
  state: {
    open: false,
    options: {
      title: 'Are you sure?',
      description: 'This action cannot be undone.',
      buttons: [
        {
          children: 'Yes',
          onClick: () => {},
        },
      ],
      closable: true,
    },
    awaitedCallback: null,
  },
  listeners: new Set<() => void>(),
  setState: (newState: Partial<ConfirmDialogState>) => {
    confirmDialogSingleton.state = {
      ...confirmDialogSingleton.state,
      ...newState,
    };
    confirmDialogSingleton.listeners.forEach((listener) => listener());
  },
  subscribe: (listener: () => void) => {
    confirmDialogSingleton.listeners.add(listener);
    return () => confirmDialogSingleton.listeners.delete(listener);
  },
};

export const ConfirmDialogProvider = () => {
  const [state, setState] = useState(confirmDialogSingleton.state);

  useEffect(() => {
    return confirmDialogSingleton.subscribe(() => {
      setState(confirmDialogSingleton.state);
    });
  }, []);

  useEffect(() => {
    if (!state.open) {
      state.awaitedCallback?.();
    }
  }, [state.open]);

  return (
    <ConfirmDialog
      {...state.options}
      key={state.options.title}
      open={state.open}
      onOpenChange={(flag, forceFlag) =>
        (state.options.closable || forceFlag) &&
        confirmDialogSingleton.setState({ open: flag })
      }
    />
  );
};

/**
 * Triggers the confirm dialog.
 *
 * @param options - The options for the confirm dialog.
 * @returns The confirm dialog and the trigger function.
 *
 * Call the trigger function to open the dialog, and pass an async callback to it.
 * The dialog will automatically go into loading state while the async callback is resolving.
 * The dialog will close when the async callback is resolved.
 *
 * @example
 * ```tsx
 * const Component = () => {
 *   const triggerConfirm = useConfirmDialog();
 *
 *   const handleRemoveTheme = async () => {
 *     await triggerConfirm({
 *       title: 'Remove theme',
 *       description: 'Are you sure you want to remove the theme?',
 *       buttons: [
 *         { children: 'Yes', onClick: () => {} },
 *         { children: 'No', onClick: () => {} },
 *       ],
 *     });
 *   };
 *
 *   return <button onClick={handleRemoveTheme}>Remove theme</button>;
 * };
 * ```
 * ```
 */
export const useConfirmDialog = () => {
  const trigger = async (options: UseConfirmDialogTriggerOptions) => {
    const awaitedCallback = new Promise<boolean>((resolve) => {
      confirmDialogSingleton.setState({
        options: { closable: true, ...options },
        open: true,
        awaitedCallback: () => {
          resolve(true);
          return Promise.resolve();
        },
      });
    });

    return awaitedCallback;
  };

  return trigger;
};
